import { Link } from 'react-router-dom'
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react'
import type { OwedEntry } from '@/api/people'
import { Button } from '@/components/ui/button'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { formatShortDate } from '@/lib/formatDate'
import { ROUTES } from '@/routes/routes'
import { transactionsLink } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { PEOPLE_CARD, PEOPLE_SECTION_LABEL } from './peopleUi'
import { sourceLabel } from './peopleStats'

export function DirectionIcon({ owedToUs, muted = false }: { owedToUs: boolean; muted?: boolean }) {
  const Icon = owedToUs ? ArrowUpRight : ArrowDownLeft
  return (
    <span
      className={cn(
        'grid h-8 w-8 shrink-0 place-items-center rounded-[10px]',
        muted
          ? 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-3)]'
          : owedToUs
            ? 'bg-[var(--fl-income-bg)] text-[var(--fl-income-text)]'
            : 'bg-[var(--fl-expense-bg)] text-[var(--fl-expense)]'
      )}
    >
      <Icon size={16} role="img" aria-label={owedToUs ? 'Owes you' : 'You owe'} />
    </span>
  )
}

/** Outstanding entries in both directions, newest first. */
export function OpenItems({
  contactId,
  entries,
  name,
  onRemoveOpening,
}: {
  contactId: string
  entries: OwedEntry[]
  name: string
  /** Omitted in read-only mode, which hides Remove. */
  onRemoveOpening?: (entry: OwedEntry) => void
}) {
  const format = useListAmountFormat()
  return (
    <section aria-label="Open">
      <h2 className={PEOPLE_SECTION_LABEL}>
        Open · {entries.length}{' '}
        <span className="font-normal normal-case tracking-normal text-[var(--fl-text-3)]">
          — cleared with Settle up
        </span>
      </h2>
      <ul className={PEOPLE_CARD}>
        {entries.map((e) => {
          const owedToUs = e.direction === 'OWED_TO_US'
          const partial = e.outstanding !== e.amount
          return (
            <li
              key={e.id}
              className="flex items-center gap-3 border-b border-[var(--fl-divider)] px-4 py-3.5 last:border-b-0"
            >
              <DirectionIcon owedToUs={owedToUs} />
              <span className="min-w-0 flex-1">
                {e.sourceType === 'OPENING' ? (
                  <span className="block truncate text-sm font-medium text-[var(--fl-text-1)]">
                    {e.description}
                  </span>
                ) : (
                  <Link
                    to={transactionsLink(
                      { date: e.entryDate, contactId },
                      { back: ROUTES.APP.PERSON_DETAIL(contactId) }
                    )}
                    className="block truncate text-sm font-medium text-[var(--fl-text-1)] hover:underline"
                  >
                    {e.description}
                  </Link>
                )}
                <span className="block text-xs text-[var(--fl-text-3)]">
                  {formatShortDate(e.entryDate)} · {sourceLabel(e, name)}
                  {e.sourceType === 'OPENING' && onRemoveOpening && !partial && (
                    <>
                      {' · '}
                      <Button
                        variant="link"
                        onClick={() => onRemoveOpening(e)}
                        className="h-auto p-0 text-xs text-[var(--fl-text-2)] underline"
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </span>
                {partial && (
                  <span
                    aria-hidden="true"
                    className="mt-1.5 block h-1 w-full max-w-[200px] overflow-hidden rounded-full bg-[var(--fl-divider)]"
                  >
                    <span
                      className={cn(
                        'block h-1 opacity-40',
                        owedToUs ? 'bg-[var(--fl-income)]' : 'bg-[var(--fl-expense)]'
                      )}
                      style={{ width: `${((e.amount - e.outstanding) / e.amount) * 100}%` }}
                    />
                  </span>
                )}
              </span>
              <span className="shrink-0 text-right">
                <span
                  className={cn(
                    'block text-sm font-semibold tabular-nums',
                    owedToUs ? 'text-[var(--fl-income)]' : 'text-[var(--fl-expense)]'
                  )}
                >
                  {format(e.outstanding)}
                </span>
                {partial && (
                  <span className="block text-[11px] text-[var(--fl-text-3)]">
                    of {format(e.amount)}
                  </span>
                )}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
